import React, { useState, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { useCompilationsAnimations } from '../hooks/gsap/useCompilationsGSAP';
import '../assets/css/compilations.css';

// Import images
import album01 from '../assets/images/album-01.jpg';
import album02 from '../assets/images/album-02.jpg';
import album03 from '../assets/images/album-03.jpg';
import { albumSources } from '../assets/images/responsiveImages';

gsap.registerPlugin(ScrollToPlugin);

/**
 * Compilation images configuration array.
 * 
 * USAGE:
 * Each image object should contain:
 * - src: Image source (required)
 * - alt: Alt text for accessibility (optional, can be null)
 * - priority: Animation priority (1-3, optional, defaults to 2)
 * - width: CSS width class (optional, defaults to "normal")
 *   Available: "thin", "small", "normal", "wide", "extra-wide"
 * - height: CSS height class (optional, defaults to "normal")
 *   Available: "extra-short", "short", "normal", "tall", "extra-tall", "auto"
 * - objectFit: CSS object-fit property (optional, defaults to "cover")
 *   Available: "cover", "contain", "fill", "scale-down", "none"
 * 
 * DEFAULTS:
 * - alt: null
 * - priority: 2 (medium priority)
 * - width: "normal"
 * - height: "auto"
 * - objectFit: "contain"
 */
// CD cover artwork only — this section is about the compilation releases.
const compilationImages = [
  { src: album01, sources: albumSources.album01, sizes: "(max-width: 495px) 70vw, 600px", alt: "Compilation 1", priority: 3, width: "extra-wide", height: "auto" },
  { src: album02, sources: albumSources.album02, sizes: "(max-width: 495px) 70vw, 450px", alt: "Compilation 2", priority: 1, width: "wide", height: "auto" },
  { src: album03, sources: albumSources.album03, sizes: "(max-width: 495px) 70vw, 350px", alt: "Compilation 3", priority: 2, width: "normal", height: "auto" },
];

const CompilationsSection = () => {
  const [topRowImages, setTopRowImages] = useState([]);
  const [bottomRowImages, setBottomRowImages] = useState([]);

  // Generate random layout on component mount
  useEffect(() => {
    const generateRandomLayout = () => {
      // Shuffle images and split into two rows
      const shuffledImages = [...compilationImages].sort(() => Math.random() - 0.5);
      const midPoint = Math.ceil(shuffledImages.length / 2);

      const topRow = shuffledImages.slice(0, midPoint);
      const bottomRow = shuffledImages.slice(midPoint);

      // Add random behind class to some images and apply defaults
      const addBehindClass = (images) => {
        return images.map(img => ({
          src: img.src,
          sources: img.sources,
          sizes: img.sizes,
          alt: img.alt || null, // Default null for alt
          priority: img.priority || 2, // Default medium priority
          width: img.width || "normal", // Default normal width
          height: img.height || "normal", // Default normal height
          objectFit: img.objectFit || "contain", // Default cover for object-fit
          behind: Math.random() > 0.5
        }));
      };

      return {
        topRow: addBehindClass(topRow),
        bottomRow: addBehindClass(bottomRow)
      };
    };

    const layout = generateRandomLayout();
    setTopRowImages(layout.topRow);
    setBottomRowImages(layout.bottomRow);
  }, []);

  useCompilationsAnimations(topRowImages, bottomRowImages);

  return (
    <section className="compilations-section section-breakpoint">
      <div className="compilations-text">
        <h1>
        <span className="first-line">Breakspoll has released</span> three official compilation albums, mixed by Breakfastaz, FreQ Nasty,
          Ed Solo and Skool of Thought. These releases stand as essential documents of the breakbeat
          scene, with the FreQ Nasty mix becoming one of the best-selling breakbeat compilations ever released.
        </h1>
      </div>
      <div className="compilations-inner">
        <div className="row--top">
          {topRowImages.map((imageConfig, index) => (
            <section
              key={`top-${index}`}
              className={`compilations-panel width--${imageConfig.width} height--${imageConfig.height} direction--right ${imageConfig.behind ? 'behind' : ''}`}
              data-priority={imageConfig.priority}
              data-index={index}
            >
              <figure>
                <picture>
                  <source
                    type="image/webp"
                    srcSet={`${imageConfig.sources.small} 360w, ${imageConfig.sources.large} ${imageConfig.sources.largeWidth}w`}
                    sizes={imageConfig.sizes}
                  />
                  <img
                    src={imageConfig.src}
                    alt={imageConfig.alt}
                    style={{ objectFit: imageConfig.objectFit }}
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </figure>
            </section>
          ))}
        </div>
        <div className="row--bottom">
          {bottomRowImages.map((imageConfig, index) => (
            <section
              key={`bottom-${index}`}
              className={`compilations-panel width--${imageConfig.width} height--${imageConfig.height} direction--right ${imageConfig.behind ? 'behind' : ''}`}
              data-priority={imageConfig.priority}
              data-index={index}
            >
              <figure>
                <picture>
                  <source
                    type="image/webp"
                    srcSet={`${imageConfig.sources.small} 360w, ${imageConfig.sources.large} ${imageConfig.sources.largeWidth}w`}
                    sizes={imageConfig.sizes}
                  />
                  <img
                    src={imageConfig.src}
                    alt={imageConfig.alt}
                    style={{ objectFit: imageConfig.objectFit }}
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </figure>
            </section>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CompilationsSection;
